import { useState } from 'react'

import { clsx } from 'clsx'

import {
  CheckIcon,
  CriticalIcon,
  Flex,
  SimpleSpinner,
  Table,
  Tag,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'

import type { ListItem } from './helpers'
import { formatPostingTime } from '../../helpers/postingTimeUtils'

import styles from './PostItem.module.css'

export const PostItem = ({
  item,
  index,
  twentyFourHourFormat,
  channelTimezone,
}: {
  item: ListItem
  index: number
  twentyFourHourFormat: boolean
  channelTimezone: string
}): JSX.Element => {
  const [hasImageError, setHasImageError] = useState(false)
  return (
    <Table.Row className={styles.postItemRow} data-status={item.status}>
      <Table.Cell className={clsx(styles.tableData, styles.indexData)}>
        {index + 1}
      </Table.Cell>
      <Table.Cell className={clsx(styles.tableData, styles.textData)}>
        <Text className={styles.itemText}>{item.data.text}</Text>
      </Table.Cell>

      <Table.Cell className={styles.tableData}>
        {item.data?.media?.photo &&
          (!hasImageError ? (
            <Flex align="center" className={styles.imageContainer}>
              <img
                className={styles.itemImage}
                src={item.data?.media?.photo}
                alt=""
                onError={() => setHasImageError(true)}
              />
            </Flex>
          ) : (
            <Flex align="center" justify="center" className={styles.itemImage}>
              <img className={styles.imageError} alt=" " />
            </Flex>
          ))}
      </Table.Cell>
      <Table.Cell className={clsx(styles.tableData, styles.tagsData)}>
        {item.data.tags.length > 0 && (
          <>
            <Tag color={item.data.tags[0].color} className={styles.tag}>
              {item.data.tags[0].name}
            </Tag>
            {item.data.tags.length > 1 && (
              <span className={styles.numberBadge}>
                {`+${item.data.tags.length - 1}`}
              </span>
            )}
          </>
        )}
      </Table.Cell>
      <Table.Cell className={clsx(styles.tableData, styles.postingTimeData)}>
        <Text className={styles.postingTimeText}>
          {formatPostingTime(
            item.data.postingTime,
            twentyFourHourFormat,
            channelTimezone,
          )}
        </Text>
      </Table.Cell>
      <Table.Cell className={clsx(styles.tableData, styles.statusData)}>
        {item.status === 'valid' ? (
          <span className={styles.itemPrefixContainer}>
            <CheckIcon color="subtle" size="small" />
          </span>
        ) : item.status === 'loading' ? (
          <span className={styles.itemPrefixContainer}>
            <SimpleSpinner />
          </span>
        ) : item.status === 'success' ? (
          <span className={styles.itemPrefixContainer}>
            <CheckIcon color="success" size="small" />
          </span>
        ) : item.status === 'error' ? (
          <span className={styles.itemPrefixContainer}>
            <Tooltip
              content={
                <Flex direction="column" gap="xs">
                  {item.errors?.map((error) => (
                    <Text size="sm" key={error}>
                      {error}
                    </Text>
                  ))}
                </Flex>
              }
            >
              <CriticalIcon color="critical" size="small" />
            </Tooltip>
          </span>
        ) : null}
      </Table.Cell>
    </Table.Row>
  )
}
